import { Component, Injector, Input, OnInit } from '@angular/core';
import { ExcelExportGridActionHandler } from '../../../../../components/common/grid-base/actions/excel-export-grid-action-handler';
import { GridActionHandlerBase } from '../../../../../components/common/grid-base/actions/grid-action-handler-base';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { OpenReportGridActionHandler } from './actions/open-report-grid-action-handler';
import { SearchGridActionHandler } from 'src/app/components/common/grid-base/actions/search-grid-action-handler';
import { DeleteReportGridActionHandler } from './actions/delete-report-grid-action-handler';
import { SyncWithArgusGridActionHandler } from './actions/sync-with-argus-grid-action-handler';
import { PrintReportGridActionHandler } from './actions/print-report-grid-action-handler';
import { TranslateService } from '@ngx-translate/core';
import { LoadDataKeepFilterGridActionHandler } from 'src/app/components/common/grid-base/actions/load-data-keep-filter-grid-action-handler';
import { LoadDataGridActionHandler } from 'src/app/components/common/grid-base/actions/load-data-grid-action-handler';
import { WorkplaceBase } from '../../../../../components/common/workplace-base/workplace-base';
import { TabConfig } from '../../../../../components/common/pollux-tab-config/pollux-form-config';
import { AppStorageService } from '../../../../../services/app-storage.service';
import { ShowAllGridActionHandler } from '../../../../../components/common/grid-base/actions/show-all-grid-action-handler';

/**
 * Reports workspace
 */
@Component({
  selector: 'reports-workspace',
  templateUrl: './reports-workspace.component.html',
  styleUrls: ['./reports-workspace.component.scss']
})
export class ReportsWorkspaceComponent extends WorkspaceBase implements OnInit {
  /**
   * JavaScript Oid property name of BO (in this case Valuation DTO)
   */
  public oidPropertyName = "Oid";

  /**
   * The parent
   */
  public parent: any = null;

  /**
   * Show the form
   * @param oid Oid of the item that should be loaded in the form. Use -1 for new mode
   */
  public showForm(oid: number): void {
    this.showGrid = false;
    this.oid = oid;

    this.eventBus.broadcastEvent('searchBarVisibility', false);
  }

  /**
   * Show form with parent selected
   * @param oid
   * @param parent
   */
  public showFormWithParent(oid: number, parent: any): void {
    this.resetGridUIState();
    this.showGrid = false;
    this.oid = oid;
    this.parent = parent;

    this.eventBus.broadcastEvent('searchBarVisibility', false);
  }

  /**
   * The workplace
   */
  private _workplace: WorkplaceBase;

  /**
   * Sets the workplace
   */
  @Input() public set workplace(workplace: WorkplaceBase) {
    this._workplace = workplace;
  }

  /**
   * Gets the workplace
   */
  public get workplace(): WorkplaceBase {
    return this._workplace;
  }

  /**
   * The tab element
   */
  private _tabElement: TabConfig;

  /**
   * Sets te tab element
   */
  @Input() public set tabElement(tabElement: TabConfig) {
    this._tabElement = tabElement;
  }

  /**
   * Gets the tab element
   */
  public get tabElement(): TabConfig {
    return this._tabElement;
  }

  /**
   * Parent report OID
   */
  private _parentReportId = -1;

  /**
   * Show Argus sync dialog?
   */
  public showArgusSyncDialog = false;

  /**
   * The selected reports
   */
  public selectedReports: number[];

  /**
   * Gets the parent report OID
   */
  public get parentReportId() {
    return this._parentReportId;
  }

  /**
   * Constructor
   * @param injector
   * @param translate
   * @param appStorage
   */
  constructor(protected injector: Injector, protected translate: TranslateService, private appStorage: AppStorageService) {
    super(injector);
  }

  /**
   * Action handlers used by this workspace area.
   */
  protected get actionHandlers(): GridActionHandlerBase[] {
    return [
      new ExcelExportGridActionHandler(this.injector, this, 'exportToExcelReportsGrid'),
      new OpenReportGridActionHandler(this.injector, this),
      new SearchGridActionHandler(this.injector, this, 'project.valuation.report.search'),
      new ShowAllGridActionHandler(this.injector, this, 'project.valuation.report.showAll'),
      new DeleteReportGridActionHandler(this.injector, this),
      new SyncWithArgusGridActionHandler(this.injector, this),
      new PrintReportGridActionHandler(this.injector, this, this.translate),
      new LoadDataKeepFilterGridActionHandler(this.injector, this, 'reportsDataSaved'),
      new LoadDataKeepFilterGridActionHandler(this.injector, this, 'reportDeleteLoadData'),
      new LoadDataKeepFilterGridActionHandler(this.injector, this, 'reportPrintLoadData'),
      new LoadDataGridActionHandler(this.injector, this, 'ReportLoadData')
    ];
  }

  /**
   * Called if the grid state was changed. Use this to notify the nav bar or perform similar actions
   * @param gridState The new grid state: true: Grid is shown, so the form isn't shown. False: Grid isn't show, but the form is shown
   */
  protected onShowGridChanged(gridState: boolean): void {
    this.eventBus.broadcastEvent('showGridChanged', { area: 'projectValuationReport', value: gridState });
  }

  /**
   * Registers any required events. Please don't forget to unsubscibe!
   */
  protected registerEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('ReportLoadData', (_payload, _options) => {
      this.grid.refreshGridClicked();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportDeleteLoadData', (_payload, _options) => {
      this.grid.refreshGridClicked();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportPrintLoadData', () => {
      this.grid.refreshGridClicked();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportCopyLoadData', () => {
      this.grid.refreshGridClicked();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportRefreshData', (_payload, _options) => {
      this.grid.showAll();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationProjectPortfolioItemCopied', (_payload, _options) => {
      this.grid.refreshGridClicked();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('cancelReportChanges', (_payload, _options) => {
      this.showGrid = true;
      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', false);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportsDataSaved', (_payload, _options) => {
      this.showGrid = true;
      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', false);
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('createReportsData', (_event, parent) => {
      this.showGrid = false;
      this.oid = -1;
      setTimeout(() => {
        this.showFormWithParent(-1, parent);
        this.eventBus.broadcastEvent('showSaveCancelNavigationAction', true);
      });
    }));
  }

  /**
   * Closes all panes
   */
  public closeAllPanes(): void {
    this.resetGridUIState();
  }

  /**
   * Applies a filter if an element was selected in a higher hierarchy area
   * @param _filter Filter settings
   * @param _selectedHierarchy Hierarchy where the element was selected
   * @param _tabConfig Tab config
   * @param reset Reset the selection in the current area
   */
  public filter(item: any, selectedHierarchy: number, tabConfig: any, reset: boolean): void {
    this._parentReportId = -1;
    if (reset) {
      this.grid.loadData({
        skip: 0
      }, null);
      return;
    }
    if (selectedHierarchy != null) {
      const filteredTab = tabConfig[selectedHierarchy];
      switch (filteredTab?.searchMode) {
        case 'projectstd': {

          this.grid.loadData({
            skip: 0,
            filter: {
              logic: "and",
              filters: [{ field: "ProjectStdId", operator: "eq", value: item.Oid }]
            }
          }, null);
          break;
        }
        case 'valuationPortfolio': {
          this.grid.loadData({
            skip: 0,
            filter: {
              logic: "and",
              filters: [{ field: "ValuationProjectId", operator: "eq", value: item.Oid }]
            }
          }, null);
          break;
        }
      }
    }
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.init();

    this.workplace.onTabCreated(this);
  }

  /**
   * Resets the grid UI state
   */
  private resetGridUIState(): void {
    this.showGrid = true;
    this.oid = null;
    this.showArgusSyncDialog = false;
  }
}
